<template>
  <main>
    <div class="row">
      <div class="col-12">
        <b-tabs v-model="filterFormModel.countryIndex" @input="load">
          <b-tab v-for="item in $store.state.countries" :key="item._id" :title="item.name"></b-tab>
        </b-tabs>
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')"></b-form-input>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting" v-if="['owner', 'admin', 'tech'].includes($store.state.user.role)">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-1"></div>
            <div class="col-lg-3">{{ $t('name') }}</div>
            <div class="col-lg-3">{{ $t('info') }}</div>
            <div class="col-lg-3">{{ $t('desc') }}</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <div class="col-lg-1">
              <figure class="c-board-table__avatar border-0">
                <img v-if="item.logoUrl" :src="item.logoUrl" class="thumbnail" alt="logo"/>
                <img v-else src="/img/default-avatar.png" class="thumbnail" alt="logo"/>
              </figure>
            </div>
            <div class="col-lg-3">
              <div><b>{{ $t('fullName') }}: </b><span>{{ item.name }}</span></div>
              <div>
                <b>{{ $t('shortName') }}: </b>
                <span v-if="item.shortName">{{ item.shortName }}</span>
                <span v-else class="text-secondary">{{ $t('unknown') }}</span>
              </div>
            </div>
            <div class="col-lg-3">
              <div><b>{{ $t('code') }}: </b><span>{{ item.code }}</span></div>
              <div>
                <b>{{ $t('website') }}: </b>
                <span v-if="item.website"><a :href="item.website" target="_blank">{{ item.website }}</a></span>
                <span v-else class="text-secondary">{{ $t('unknown') }}</span>
              </div>
              <div>
                <span v-if="item.country" class="badge badge-info">{{ item.country.name }}</span>&nbsp;
                <span v-if="item.integration" class="badge badge-success">{{ $t('integration') }}</span>
              </div>
            </div>
            <div class="col-lg-3">
              <p v-if="item.desc">{{ item.desc }}</p>
            </div>
            <div class="col-lg-2 text-right">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix is-2">
                  <div class="c-buttons__col" v-if="['owner', 'admin', 'tech'].includes($store.state.user.role)">
                    <button @click="update(item._id)" type="button" class="btn btn-sm btn-info mr-1">
                      <font-awesome-icon icon="edit"/>&nbsp;{{ $t('edit') }}
                    </button>
                    <button @click="del(item._id)" type="button"
                            v-if="['owner', 'admin'].includes($store.state.user.role)"
                            class="btn btn-sm btn-danger mr-1">
                      <font-awesome-icon icon="trash"/>&nbsp;{{ $t('delete') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length">
        <span v-if="filterFormModel.page === 1 && !filterFormModel.keyword">{{ $t('notFoundCreate') }}</span>
        <span v-if="filterFormModel.page > 1 || filterFormModel.keyword">{{ $t('notFoundSearch') }}</span>
      </div>

      <div class="col-12" v-if="itemCount">
        <div class="c-paging__count" v-if="itemCount">
          {{ $t('found', [itemCount]) }}
        </div>
        <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                      v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                      @input="load">
        </b-pagination>
      </div>
    </div>
    <b-modal v-model="showForm" size="lg" :title="formTitle" @ok="save" no-close-on-backdrop no-enforce-focus>
      <div class="row">
        <div class="col-md-12">
          <country-select :model="formModel" :errors="formErrors"></country-select>
          <form-input :label="$t('fullName')" :model="formModel" attribute="name" :errors="formErrors"></form-input>
          <form-input :label="$t('shortName')" :model="formModel" attribute="shortName"
                      :errors="formErrors"></form-input>
          <form-input :disabled="formModel.id" :label="$t('code')" :model="formModel" attribute="code"
                      :errors="formErrors"></form-input>
          <form-input :label="$t('website')" :model="formModel" attribute="website"
                      :errors="formErrors"></form-input>
          <form-textarea row="3" :label="$t('desc')" :model="formModel" attribute="desc" :errors="formErrors"></form-textarea>
          <form-file label="Logo" service="/upload/carrier-gateway" :model="formModel"
                     attribute="logoHash" attribute-file-type="logoType" :errors="formErrors"
                     accept="image/png,image/jpeg,image/jpg,application/pdf">
          </form-file>
          <form-checkbox :chk-label="$t('integration')" :model="formModel" attribute="integration"
                         :errors="formErrors"></form-checkbox>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import List from '../../mixins/List';
import CRUD from "../../mixins/CRUD";
import Utils from '../../utils';
import CountrySelect from "@/components/CountrySelect.vue";

export default {
  components: {CountrySelect},
  mixins: [List, CRUD],
  data: function () {
    return {
      service: '/carrier-gateway',
      autoLoad: true,
      defaultFormModel: {
        name: null,
        shortName: null,
        code: null,
        desc: null,
        country: null,
        website: null,
        integration: false,
        countryIndex: 0,
      },
      imageLoading: false,
    }
  },
  i18n: {
    messages: {
      en: {
        code: 'Code',
        fullName: 'Full name',
        shortName: 'Short name',
        website: 'Website',
        integration: 'Code tích hợp',
      },
      vi: {
        code: 'Mã',
        fullName: 'Tên đầy đủ',
        shortName: 'Tên viết tắt',
        website: 'Trang web',
        integration: 'Code tích hợp',
      }
    }
  },
  watch: {
    "formModel.name": {
      handler(val) {
        if (!this.formModel.id) {
          if (val) {
            this.formModel.code = Utils.methods.createAlias(val);
          }
        }
      }
    },
  },
  methods: {
    load: function () {
      this.filterFormModel.country = this.getCountryId(this.filterFormModel.countryIndex);
      let loader = this.$loading.show();
      this.loading = true;
      this.listLoading = true;
      this.$service.get(this.service, {
        params: this.filterFormModel
      }).then(response => {
        this.apply(response.data);
      }).catch(error => {
        if (error.response.data.message) {
          this.$swal({
            text: error.response.data.message,
            icon: 'error'
          });
        }
      }).finally(() => {
        loader.hide();
        this.loading = false;
        this.listLoading = false;
        if (this.afterLoad) {
          this.afterLoad();
        }
      });
    },
    getCountryId: function (index) {
      let country = this.$store.state.countries[index];
      return country ? country._id : null;
    },
    create: function () {
      this.formModel = {};
      this.formModel = Object.assign({}, this.formModel, this.defaultFormModel);
      this.formModel.country = this.filterFormModel.country;
      this.formTitle = this.$t('create');
      this.formErrors = [];
      this.showForm = true;
    },
  }
};
</script>